import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLogger } from "@/hooks/useLogger";
import { createPaymentTracker } from "@/utils/payment-tracking";
import { GridPageLayout } from "@/components/general/grid-page-layout/GridPageLayout";
import { NavButtons } from "@/components/general/nav-buttons/NavButtons";
import { Section } from "@/components/general/section";
import { TransactionStatus } from "@/components/transaction/transaction-status/TransactionStatus";
import { TransactionStatusSkeleton } from "@/components/transaction/transaction-status/transaction-status-skeleton/TransactionStatusSkeleton";
import { useGetTransactionsStatus } from "@/hooks/useGetTransactionStatus";
import { useTradingAccounts } from "@/hooks/useTradingAccounts";
import { GLOBAL_NAVIGATION_URLS, globalNavigation } from "@/utils/constants";
import { ERoutes } from "@/utils/enums";
import { moneyToMajorUnits } from "@/utils/helpers";
import type {
  CashierTradingAccount,
  AmountCurrencyResponse,
} from "@/api/cashier";
import { EnumCashierTxStatus } from "@/api/cashier";

interface CompletionProps {
  transactionType: "deposit" | "withdrawal";
}
export const Completion = ({ transactionType }: CompletionProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const logger = useLogger();
  const tracker = useMemo(() => createPaymentTracker(logger), [logger]);

  // Transaction Reference ID
  const { id: referenceId } = useParams();
  const location = useLocation();
  const bankTransferState = (
    location.state as {
      bankTransfer?: {
        account: CashierTradingAccount;
        money: AmountCurrencyResponse;
      };
    } | null
  )?.bankTransfer;
  const {
    data: accounts,
    isLoading: isAccountsLoading,
    isFetching: isAccountsFetching,
    refetch: refetchAccounts,
  } = useTradingAccounts();
  const [pendingToAccounts, setPendingToAccounts] = useState(false);
  const scrolledOnceRef = useRef(false);
  const sectionRef = useRef<HTMLElement | null>(null);
  const {
    data: polledTransactionStatus,
    isLoading: isTransactionStatusLoading,
  } = useGetTransactionsStatus({
    transactionType,
    // For bank transfer there is no referenceId — the hook is disabled for empty string
    referenceId: referenceId ?? "",
  });

  const transactionStatus = bankTransferState
    ? {
        status: EnumCashierTxStatus.CashierTxPending,
        money: bankTransferState.money,
        balance: bankTransferState.account.balance,
        external_trading_account_id: String(
          bankTransferState.account.accountId
        ),
      }
    : polledTransactionStatus;

  const account = bankTransferState
    ? bankTransferState.account
    : accounts?.find(
        (account) =>
          account.accountId.toString() ===
          transactionStatus?.external_trading_account_id // TODO verify on BE this should be the "loginId" instead
      );

  const isCancelled = transactionStatus?.status === "Cancelled";
  const isFailed = transactionStatus?.status === "Unsuccessful";
  // Both states use the same "try again / change method" CTA layout, but we
  // track them separately and may grow the copy/UI apart in future.
  const isUnsuccessful = isFailed || isCancelled;

  // Eagerly refetch trading accounts when a deposit completes so the updated
  // balance is ready before the user navigates back to the account selector.
  useEffect(() => {
    if (transactionStatus?.status !== EnumCashierTxStatus.CashierTxComplete)
      return;
    refetchAccounts();
  }, [transactionStatus?.status, refetchAccounts]);

  // Once the user has requested navigation to accounts, wait for the in-flight
  // refetch to finish before actually navigating.
  useEffect(() => {
    if (!pendingToAccounts || isAccountsFetching) return;
    navigate(`../${ERoutes.Accounts}`);
  }, [pendingToAccounts, isAccountsFetching, navigate]);

  // Track deposit failures only (not success - backend should handle that)
  useEffect(() => {
    if (!transactionStatus || !account || transactionType !== "deposit") return;

    const methodId =
      "payment_method_id" in transactionStatus &&
      typeof transactionStatus.payment_method_id === "string"
        ? transactionStatus.payment_method_id
        : "unknown";

    if (isCancelled) {
      tracker.deposit.cancelled({
        methodId,
        referenceId: referenceId ?? undefined,
        step: "thank_you_page",
      });
      return;
    }

    if (isFailed) {
      tracker.deposit.failed({
        methodId,
        amount: moneyToMajorUnits(transactionStatus.money) ?? undefined,
        error: transactionStatus.status,
        errorCode: transactionStatus.status?.toUpperCase(),
      });
    }
  }, [
    transactionStatus,
    account,
    transactionType,
    isFailed,
    isCancelled,
    referenceId,
    tracker,
  ]);

  useEffect(() => {
    if (
      isTransactionStatusLoading ||
      isAccountsLoading ||
      scrolledOnceRef.current
    )
      return;
    if (!account || !transactionStatus) return;
    scrolledOnceRef.current = true;
    sectionRef.current
      ?.closest(".cashier")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [
    isTransactionStatusLoading,
    isAccountsLoading,
    account,
    transactionStatus,
  ]);

  if (isTransactionStatusLoading || isAccountsLoading)
    return <TransactionStatusSkeleton />;

  if (!account || !transactionStatus || (!referenceId && !bankTransferState))
    return;

  return (
    <GridPageLayout>
      <Section ref={sectionRef}>
        <TransactionStatus
          account={account}
          transactionType={transactionType}
          transactionStatus={transactionStatus}
        />
        <NavButtons
          nextLabel={
            isUnsuccessful
              ? t("global.nav_buttons.try_again")
              : t("global.nav_buttons.trade_now")
          }
          backLabel={
            isUnsuccessful
              ? t("global.nav_buttons.change_method")
              : t("global.nav_buttons.deposit_more_funds")
          }
          backIsLoading={
            !isUnsuccessful && pendingToAccounts && isAccountsFetching
          }
          nextAction={() => {
            isUnsuccessful
              ? navigate(`../${ERoutes.ChooseMethod}`)
              : globalNavigation(GLOBAL_NAVIGATION_URLS.TRADING_PLATFORMS);
          }}
          backAction={() => {
            if (isUnsuccessful) {
              navigate(`../${account.accountLoginId}/${ERoutes.ChooseMethod}`);
            } else {
              setPendingToAccounts(true);
            }
          }}
        />
      </Section>
    </GridPageLayout>
  );
};
